import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@/components/Icon';
import { api } from '@/lib/api';
import { usePlan } from '@/hooks/usePlan';
import { useSupportAddon } from '@/hooks/useSupportAddon';
import { useReservationsAddon } from '@/hooks/useReservationsAddon';
import { useNotifications } from '@/hooks/useNotifications';
import { Modal, ModalType } from '@/components/Modal';
import { SupportChat } from '@/components/SupportChat';

interface ModalState {
  isOpen: boolean
  title: string
  message: string
  type: ModalType
  onConfirm?: () => void
  confirmText?: string
}

export const Layout: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, tenant, logout, setAuth } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { hasFeature } = usePlan();

  // Keep the persisted auth store in sync with the server-side tenant plan.
  // The plan may change after an upgrade/downgrade via the Change Plan API,
  // and the store is only updated on login/register/settings otherwise.
  const tenantId = tenant?.id;
  const { data: profileData } = useQuery({
    queryKey: ['user-profile', tenantId],
    queryFn: async () => {
      const response = await api.get('/auth/me');
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!profileData?.tenant) return;
    if (!tenant || profileData.tenant.plan === tenant.plan) return;

    setAuth({
      user: profileData.user || user!,
      tenant: profileData.tenant,
      accessToken: useAuthStore.getState().accessToken!,
      refreshToken: useAuthStore.getState().refreshToken!,
    });
  }, [profileData, tenant, user, setAuth]);

  const { unreadCount, newLeadsCount, isSupported, permission, isSubscribed, actionResult, subscribe, unsubscribe, resetActionResult } = useNotifications();

  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  })

  // Show modal when actionResult changes
  useEffect(() => {
    if (!actionResult) return

    const { action, success } = actionResult

    if (action === 'subscribe' && success) {
      setModal({
        isOpen: true,
        title: t('notifications.successTitle') || 'Notifications Enabled',
        message: t('notifications.successMessage') || 'You will now receive push notifications for new conversations and leads.',
        type: 'success',
      })
    } else if (action === 'subscribe' && !success) {
      setModal({
        isOpen: true,
        title: t('notifications.errorTitle') || 'Enable Failed',
        message: t('notifications.errorMessage') || 'Could not enable notifications. Please try again.',
        type: 'error',
      })
    } else if (action === 'unsubscribe' && success) {
      setModal({
        isOpen: true,
        title: t('notifications.disabledTitle') || 'Notifications Disabled',
        message: t('notifications.disabledMessage') || 'You will no longer receive push notifications.',
        type: 'info',
      })
    } else if (action === 'blocked') {
      setModal({
        isOpen: true,
        title: t('notifications.blockedTitle') || 'Notifications Blocked',
        message: t('notifications.blockedMessage') || 'Please enable notifications in your browser settings to receive alerts.',
        type: 'error',
      })
    } else if (action === 'unsupported') {
      setModal({
        isOpen: true,
        title: t('notifications.unsupportedTitle') || 'Not Supported',
        message: t('notifications.unsupportedMessage') || 'Push notifications are not supported in this browser.',
        type: 'error',
      })
    }

    resetActionResult()
  }, [actionResult, t, resetActionResult])

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }))
  }

  // Show a blocking modal when the API rejects a write due to expired trial
  // (403 TRIAL_EXPIRED, dispatched by the axios interceptor in lib/api.ts)
  const lastTrialModalAt = useRef(0)
  useEffect(() => {
    const handler = (e: Event) => {
      const now = Date.now()
      if (now - lastTrialModalAt.current < 5000) return // throttle repeated failures
      lastTrialModalAt.current = now

      const serverMessage = (e as CustomEvent).detail?.message
      const blockCode = (e as CustomEvent).detail?.code
      setModal({
        isOpen: true,
        title: blockCode === 'SUBSCRIPTION_SUSPENDED'
          ? t('billing.subscriptionInactiveTitle')
          : t('billing.trialExpiredTitle'),
        message: serverMessage || t('billing.trialExpiredMessage'),
        type: 'confirm',
        onConfirm: () => {
          closeModal()
          navigate('/billing')
        },
        confirmText: t('billing.subscribeNow'),
      })
    }
    window.addEventListener('r3c:trial-expired-blocked', handler)
    return () => window.removeEventListener('r3c:trial-expired-blocked', handler)
  }, [t, navigate])

  // Handle toggle click - show confirmation modal first
  const handleToggleNotifications = () => {
    if (isSubscribed) {
      setModal({
        isOpen: true,
        title: t('notifications.disableConfirmTitle') || 'Disable Notifications?',
        message: t('notifications.disableConfirmMessage') || 'You will no longer receive alerts for new conversations and leads.',
        type: 'confirm',
        onConfirm: () => {
          closeModal()
          unsubscribe()
        },
        confirmText: t('notifications.disable') || 'Disable',
      })
    } else if (permission === 'denied') {
      setModal({
        isOpen: true,
        title: t('notifications.blockedTitle') || 'Notifications Blocked',
        message: t('notifications.blockedInstructions') || 'You previously blocked notifications. Please enable them in your browser settings (usually in the lock icon next to the URL bar), then try again.',
        type: 'error',
      })
    } else {
      setModal({
        isOpen: true,
        title: t('notifications.enableConfirmTitle') || 'Enable Push Notifications?',
        message: t('notifications.enableConfirmMessage') || 'Get instant alerts when someone starts a conversation or submits a lead. Your browser will ask for permission next.',
        type: 'confirm',
        onConfirm: () => {
          closeModal()
          setTimeout(() => subscribe(), 300)
        },
        confirmText: t('notifications.enable') || 'Enable',
      })
    }
  }

  // Trial banner logic
  const getTrialDaysLeft = () => {
    if (!tenant?.trialEndsAt || tenant.plan !== 'trial') return null;
    const endsAt = new Date(tenant.trialEndsAt);
    const now = new Date();
    const diffMs = endsAt.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const trialDaysLeft = getTrialDaysLeft();
  const isTrialExpired = trialDaysLeft !== null && trialDaysLeft <= 0;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const allNavItems = [
    { path: '/', label: t('nav.dashboard'), icon: 'chart-line', feature: 'chat' },
    { path: '/widget', label: t('nav.widget'), icon: 'comments', feature: 'chat' },
    { path: '/conversations', label: t('nav.conversations'), icon: 'message', feature: 'chat' },
    { path: '/leads', label: t('nav.leads'), icon: 'bullseye', feature: 'leads' },
    { path: '/proposals', label: t('nav.proposals'), icon: 'file-lines', feature: 'proposals' },
    { path: '/analytics', label: t('nav.analytics'), icon: 'chart-column', feature: 'analytics' },
    { path: '/billing', label: t('nav.subscription'), icon: 'credit-card', feature: 'chat' },
    { path: '/integrations', label: t('nav.integrations'), icon: 'plug', feature: 'chat' },
    { path: '/settings', label: t('nav.settings'), icon: 'gear', feature: 'chat' },
    { path: '/team', label: t('nav.team'), icon: 'users', feature: 'chat' },
    { path: '/support', label: t('nav.support'), icon: 'headset', feature: 'chat' },
    { path: '/help', label: t('nav.help'), icon: 'circle-question', feature: 'chat' },
  ];

  const navItems = allNavItems.filter((item) => hasFeature(item.feature));

  // Support add-on: always visible so tenants can discover it, even when
  // inactive. Clicking it when inactive shows the add-on upsell page.
  // Place it right above the regular Support entry.
  const { active: supportAddonActive } = useSupportAddon();
  const supportModuleItem = { path: '/support-module', label: t('nav.supportModule'), icon: 'puzzle-piece', feature: 'chat' }
  const supportIndex = navItems.findIndex((item) => item.path === '/support')
  const insertIndex = supportIndex >= 0 ? supportIndex : navItems.length - 1
  navItems.splice(insertIndex, 0, supportModuleItem)

  // Reservations add-on: pre-release — only visible to superadmins (bypass)
  // or tenants with the add-on active. Hidden from everyone else.
  const { visible: reservationsVisible, active: reservationsAddonActive } = useReservationsAddon();
  if (reservationsVisible) {
    navItems.splice(insertIndex, 0, { path: '/reservations', label: t('nav.reservations'), icon: 'calendar-check', feature: 'chat' })
  }

  return (
    <div className="flex h-screen bg-[#0a0a0f] text-white">
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={t('common.cancel')}
      />

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#12121a] border-r border-white/10 flex flex-col transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="p-6 flex flex-col items-center gap-3">
          <a href="https://r3converse.com" target="_blank" rel="noopener noreferrer">
            <img src="/logo-r3converse.svg" alt="r3Converse" className="h-8 w-auto" />
          </a>
          {tenant?.name && (
            <span className="inline-flex items-center rounded-full bg-[#006cd4]/20 px-3 py-1 text-xs font-medium text-[#006cd4] ring-1 ring-inset ring-[#006cd4]/20">
              {tenant.name}
            </span>
          )}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-4 right-4 p-2 text-gray-400 hover:text-white"
            aria-label={t('nav.closeMenu')}
          >
            <Icon name="xmark" className="text-lg" />
          </button>
        </div>

        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-[#006cd4]/20 text-[#006cd4]'
                    : item.path === '/support-module' || item.path === '/reservations'
                      ? 'bg-[#006cd4]/5 text-gray-500 hover:bg-[#006cd4]/10 hover:text-white'
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <Icon name={item.icon} className="w-5 text-center" />
              <span className="flex-1">{item.label}</span>
              {(item.path === '/support-module' || item.path === '/reservations') && (
                (item.path === '/support-module' ? supportAddonActive : reservationsAddonActive) ? (
                  <span className="inline-flex items-center gap-1 text-xs text-green-400">
                    <Icon name="circle" className="w-2 h-2 text-green-400" />
                  </span>
                ) : (
                  <Icon name="lock" className="w-3.5 text-gray-500" />
                )
              )}
              {item.path === '/conversations' && unreadCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-red-500 rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              {item.path === '/leads' && newLeadsCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-red-500 rounded-full">
                  {newLeadsCount > 99 ? '99+' : newLeadsCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          {isSupported && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2 px-2">
                <Icon name="bell" className="w-3.5 text-gray-500" />
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('notifications.title') || 'Notifications'}
                </span>
              </div>
              <div className="flex items-center justify-between px-2 py-2">
                <div className="flex flex-col">
                  <span className={`text-sm font-medium ${
                    isSubscribed ? 'text-green-400' : permission === 'denied' ? 'text-red-400' : 'text-gray-300'
                  }`}>
                    {isSubscribed
                      ? t('notifications.pushOn') || 'Push On'
                      : permission === 'denied'
                        ? t('notifications.pushBlocked') || 'Push Blocked'
                        : t('notifications.pushOff') || 'Push Off'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {isSubscribed
                      ? t('notifications.receiving') || 'Receiving alerts'
                      : permission === 'denied'
                        ? t('notifications.clickToFix') || 'Click to fix'
                        : t('notifications.notReceiving') || 'Not receiving alerts'}
                  </span>
                </div>
                <button
                  onClick={handleToggleNotifications}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isSubscribed
                      ? 'bg-green-500'
                      : permission === 'denied'
                        ? 'bg-red-500/50 hover:bg-red-500/70'
                        : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                  title={
                    permission === 'denied'
                      ? t('notifications.blockedTooltip') || 'Blocked in browser settings — click for help'
                      : isSubscribed
                        ? t('notifications.enabledTooltip') || 'Click to disable'
                        : t('notifications.disabledTooltip') || 'Click to enable'
                  }
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isSubscribed ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#006cd4]/20 flex items-center justify-center text-[#006cd4]">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || t('common.anonymous')}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Icon name="right-from-bracket" className="w-4" />
            {t('nav.logout')}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto min-w-0">
        {trialDaysLeft !== null && (
          <div className={`px-4 py-2 text-center text-sm ${
            isTrialExpired 
              ? 'bg-red-500/20 text-red-400 border-b border-red-500/20' 
              : trialDaysLeft <= 2 
                ? 'bg-yellow-500/20 text-yellow-400 border-b border-yellow-500/20'
                : 'bg-[#006cd4]/20 text-[#006cd4] border-b border-[#006cd4]/20'
          }`}>
            {isTrialExpired ? (
              <span>
                {t('billing.expiredBanner')}{' '}
                <button onClick={() => navigate('/billing')} className="underline font-semibold cursor-pointer hover:opacity-80">
                  {t('billing.subscribeNow')}
                </button>{' '}
                {t('billing.expiredSuffix')}
              </span>
            ) : (
              <span>
                {t('billing.daysLeftBanner', { count: trialDaysLeft })}{' '}
                <button onClick={() => navigate('/billing')} className="underline font-semibold cursor-pointer hover:opacity-80">
                  {t('billing.subscribeNow')}
                </button>
              </span>
            )}
          </div>
        )}

        <div className="lg:hidden flex items-center gap-4 p-4 bg-[#12121a] border-b border-white/10">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-gray-400 hover:text-white"
            aria-label={t('nav.openMenu')}
          >
            <Icon name="bars" className="text-lg" />
          </button>
          <a href="https://r3converse.com" target="_blank" rel="noopener noreferrer">
            <img src="/logo-r3converse.svg" alt="r3Converse" className="h-6 w-auto" />
          </a>
        </div>

        <div
          className="p-4 lg:p-8 min-h-full bg-no-repeat bg-right-top"
          style={{ backgroundImage: 'url(/patron-dashboard.png)', backgroundAttachment: 'fixed' }}
        >
          <Outlet />
        </div>
      </main>

      <SupportChat />
    </div>
  );
};
